import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniOtpInput } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ChangeATMPINStackParamList, ChangeATMPINStackRoutes } from './type';
import { OtpInputVariant } from '@/design-system/components/inputs/uniOtpInput';
import { Card } from '../../../../../../packages/utx-access-client/src/react';

export interface ChangeATMPINProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  account: string;
  tab?: number;
  record?: Card;
  func?: (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
}

export const ChangeATMPINWebZeroScreen: React.FC<ChangeATMPINProps> = (props) => {
  const { onClose, onLayout, onData, account, tab, record, func, onReturnData } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, checkOnView, dotsymbolView, textCheckOn, textDotsymbol, btnView, btn } = sxStyles;

  const [pin, setPin] = useState<string | undefined>(undefined);
  const [rePin, setRePin] = useState<string | undefined>(undefined);
  const [repeatedNumber, setRepeatedNumber] = useState<boolean>(false);
  const [bothMatch, setBothMatch] = useState<boolean>(false);

  const navigation = useNavigation<NativeStackNavigationProp<ChangeATMPINStackParamList>>();

  const changeAtmPin = (submit = true, param = {}) => {
    let pinFlag = pin;
    let rePinFlag = rePin;
    if (Object.keys(param).length > 0) {
      if (param.field == 'pin') {
        setPin(param.value);
        pinFlag = param.value;
      } else if (param.field == 'rePin') {
        setRePin(param.value);
        rePinFlag = param.value;
      }
    }

    setRepeatedNumber(false);
    setBothMatch(false);
    var reg_repeated = /(.)\1{2}/;
    var reg_consecutive = /(.)(0(?=1)|1(?=2)|2(?=3)|3(?=4)|4(?=5)|5(?=6)|6(?=7)|7(?=8)|8(?=9)){2}/;
    if (!pinFlag || pinFlag.length < 6) {
      return;
    } else if (reg_repeated.test(pinFlag) || reg_consecutive.test(pinFlag)) {
      return;
    } else {
      setRepeatedNumber(true);
      if (pinFlag != rePinFlag) {
        return;
      } else {
        setBothMatch(true);
        if (submit) {
          onData({ pin });
          navigation.navigate(ChangeATMPINStackRoutes.Confirm, { onLayout, data: { pin }, account, tab, record, func, onReturnData });
        }
      }
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader title={t('card-account-management::card-details.text--change-atm-pin')} closeModal={onClose} />
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::change-atm-pin.text--create-new-pin')}
        </TextBody>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::change-atm-pin.text--enter-new-pin')}
        </TextCaption>
        <View sx={textMb}>
          <UniOtpInput onValueChange={(value) => changeAtmPin(false, { field: 'pin', value: value })} variant={OtpInputVariant.Rounded} />
        </View>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::change-atm-pin.text--re-enter-new-pin')}
        </TextCaption>
        <UniOtpInput onValueChange={(value) => changeAtmPin(false, { field: 'rePin', value: value })} variant={OtpInputVariant.Rounded} />
        <View sx={checkOnView}>
          <Icon name={repeatedNumber ? 'checkOn' : 'dotsymbol'} />
          <TextCaption sx={{ ...textCheckOn, color: repeatedNumber ? '$success' : '$textSecondary' }}>
            {t('card-account-management::change-atm-pin.text--no-consecutive-repeated-number')}
          </TextCaption>
        </View>
        <View sx={dotsymbolView}>
          <Icon name={bothMatch ? 'checkOn' : 'dotsymbol'} />
          <TextCaption sx={{ ...textDotsymbol, color: bothMatch ? '$success' : '$textSecondary' }}>
            {t('card-account-management::change-atm-pin.text--both-pin-match')}
          </TextCaption>
        </View>
        <View sx={btnView}>
          <UniButton
            sx={btn}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::change-atm-pin.button--update')}
            onPress={changeAtmPin}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4
  },
  checkOnView: {
    marginTop: 13,
    flexDirection: 'row',
    alignItems: 'center'
  },
  dotsymbolView: {
    marginTop: 2,
    flexDirection: 'row',
    alignItems: 'center'
  },
  textCheckOn: {
    marginLeft: 10,
    fontWeight: 'body',
    color: '$success'
  },
  textDotsymbol: {
    marginLeft: 12,
    fontWeight: 'body',
    color: '$textSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
